import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import {
  BodyText,
  DisplayText,
  Modal,
  ScrollViewWithHeaderTitle,
  theme,
  useNavbarContainerHeight,
  useResponsive,
} from '@sg/garnish';
import { ModifierGroupVersion } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { setSharedLineItemAttribution } from '@order/shared-line-item';
import { useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { AppFooter, NotFoundView } from '../../components';
import { ErrorFallback } from '../../components/ErrorBoundary';
import { SharedLineItem } from '../../components/SharedLineItem';
import { useLastInteractedStore } from '../../context';
import { useCart } from '../../hooks';
import { useReorderNavigation } from '../../hooks/useReorderNavigation';
import {
  PartnerShareLineItemScreen,
  ShareLineItemLoadingPlaceholder,
  ShareLineItemLocationAlert,
} from './components';
import {
  type GetSharedLineItemQuery,
  useGetSharedLineItemProductModifierGroupsQuery,
  useGetSharedLineItemQuery,
} from './GraphQL/ShareLineItemScreen.generated';
import {
  usePartnerShareLineItemContent,
  useShareLineItemNavigation,
  useShareLineItemQuickAddToBag,
  useShareLineItemRemoteData,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ShareLineItemScreenV2 = () => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { lineItemSlug } = useLocalSearchParams<ShareLineItemRouteParams>();
  const navigation = useNavigation();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ fetching, data }] = useGetSharedLineItemQuery({
    variables: { input: { slug: lineItemSlug } },
    requestPolicy: 'network-only',
  });

  // ─── Data ────────────────────────────────────────────────────────────

  const lineItemBySlug = data?.lineItemBySlug;
  const hasLineItemData = lineItemBySlug?.__typename === 'LineItemSuccess';
  const isLineItemNotFound = lineItemBySlug?.__typename === 'LineItemNotFound';
  const lineItem = hasLineItemData ? lineItemBySlug.lineItem : undefined;
  const shouldHideHeaderWhileLoading = fetching && currentBreakpoint.isXS;

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    const shouldDeferToContent = !fetching && Boolean(lineItem);

    if (shouldDeferToContent) return;

    navigation.setOptions({ headerShown: !shouldHideHeaderWhileLoading });
  }, [fetching, lineItem, navigation, shouldHideHeaderWhileLoading]);

  // ─────────────────────────────────────────────────────────────────────

  if (fetching) {
    return <ShareLineItemLoadingPlaceholder />;
  }

  if (isLineItemNotFound) {
    return <NotFoundView />;
  }

  if (!lineItem) {
    return <ErrorFallback error={formatMessage(messages.shareLineItemError)} />;
  }

  return <ShareLineItemContent lineItem={lineItem} lineItemSlug={lineItemSlug} />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ShareLineItemContent = (props: ShareLineItemScreenV2ContentProps) => {
  const { lineItem, lineItemSlug } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint, match, minWidth } = useResponsive();
  const { bottom } = useSafeAreaInsets();
  const navigation = useNavigation();
  const { height: navbarHeight } = useNavbarContainerHeight();

  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
  const isPartnerShareScreenEnabled = useFeatureFlag(
    'CELS-5713-order-partner-share-screen-enabled',
  );
  const isQuickAddEnabled = useFeatureFlag('CELS-5657-order-quick-add-modifiable-product-enabled'); // prettier-ignore

  const {
    content: partnerContent,
    fetching: isFetchingPartnerContent,
    shouldDisplayCategories,
  } = usePartnerShareLineItemContent({ lineItemSlug, pause: !isPartnerShareScreenEnabled });

  const [productModifierGroupsResponse] = useGetSharedLineItemProductModifierGroupsQuery({
    pause: !isPartnerShareScreenEnabled || !shouldDisplayCategories,
    requestPolicy: 'network-only',
    variables: { id: lineItem.product.id, modifierGroupVersion: ModifierGroupVersion.V2 },
  });

  const lastInteractedStore = useLastInteractedStore();
  const { isCartEmpty } = useCart();
  const { finishReorder } = useReorderNavigation();
  const { track } = useTelemetry();

  // ─── Derived ─────────────────────────────────────────────────────────

  const cardLineItem = getCardLineItem(lineItem);
  const productName = lineItem.customName ?? lineItem.product.name;
  const sharedLineItemProductName = lineItem.product.name;
  const baseProductSlug = lineItem.product.baseProduct?.slug ?? lineItem.product.slug;
  const baseProductLineageKey = lineItem.product.baseProduct?.lineageKey ?? undefined;
  const selectedRestaurantId = lastInteractedStore?.restaurantId;
  const selectedDeliveryAddressId = lastInteractedStore?.addressId;
  const productModifierGroups = shouldDisplayCategories
    ? (productModifierGroupsResponse.data?.product.modifierGroups ?? [])
    : [];
  const hasLocation = Boolean(selectedRestaurantId);

  // ─────────────────────────────────────────────────────────────────────

  const {
    isFetchingSharedLinkProduct,
    isUnavailableProduct,
    locationDetails,
    productForRestaurant,
  } = useShareLineItemRemoteData({
    baseProductLineageKey,
    baseProductSlug,
    selectedDeliveryAddressId,
    selectedRestaurantId,
  });

  const { navigateToCustomization, navigateToLocationSearch, navigateToQuickAdd } =
    useShareLineItemNavigation({
      baseProductLineageKey,
      lineItemSlug,
      productForRestaurant,
      selectedDeliveryAddressId,
      selectedRestaurantId,
      sharedLineItemId: lineItem.id,
    });

  const handleQuickAddToBagSuccess = useCallback(() => {
    if (selectedDeliveryAddressId) {
      finishReorder(undefined, selectedDeliveryAddressId);
      return;
    }

    finishReorder(productForRestaurant?.restaurant.slug);
  }, [finishReorder, productForRestaurant?.restaurant.slug, selectedDeliveryAddressId]);

  const { isAddingProductToBag, quickAddToBag } = useShareLineItemQuickAddToBag({
    deliveryDetails: locationDetails?.deliveryOrderDetail,
    lineItemQuantity: lineItem.quantity,
    sharedLineItemSlug: lineItemSlug,
    navigateToLocationSearch,
    onAddProductToBagSuccess: handleQuickAddToBagSuccess,
    productForRestaurant,
  });

  // ─── Flags ───────────────────────────────────────────────────────────

  const isModifiableProduct = productForRestaurant?.isModifiable === true;
  const shouldShowLocationPrompt = !hasLocation;
  const shouldShowUnavailablePrompt = hasLocation && isUnavailableProduct;
  const shouldDisableAddToBag = isUnavailableProduct && !isCartEmpty;
  const canQuickAddModifiableProduct = isQuickAddEnabled && isModifiableProduct;
  const canOrderProduct = hasLocation && !isUnavailableProduct && Boolean(productForRestaurant);
  const shouldShowCustomize = canOrderProduct && canQuickAddModifiableProduct;

  const shouldShowPartnerScreenHeader = Boolean(partnerContent) && currentBreakpoint.isXS;

  const isFetchingProductModifierGroups = shouldDisplayCategories && productModifierGroupsResponse.fetching; // prettier-ignore
  const isLoading = isFetchingSharedLinkProduct || isFetchingPartnerContent || isFetchingProductModifierGroups; // prettier-ignore

  const shouldHideHeaderWhileLoading = isLoading && currentBreakpoint.isXS;
  const shouldApplyNavbarSpacing = isNavbarV2Enabled && minWidth.isSM;

  // ─────────────────────────────────────────────────────────────────────

  const addToBagText =
    shouldShowUnavailablePrompt && !shouldDisableAddToBag
      ? formatMessage(messages.changeLocationCta)
      : undefined;

  const unavailableProductMessage = shouldDisableAddToBag
    ? formatMessage(messages.unavailableProductForOrderMessage)
    : formatMessage(messages.unavailableProductMessage);

  // ─── Styles ──────────────────────────────────────────────────────────

  const scrollContentContainerBottomInsetStyles = useStyle(
    () => ({ paddingBottom: Math.max(bottom, theme.spacing['10']) }),
    [bottom],
  );
  const scrollContentContainerNavbarStyles = useStyle(
    () =>
      shouldApplyNavbarSpacing ? { paddingTop: navbarHeight + theme.spacing['10'] } : undefined,
    [navbarHeight, shouldApplyNavbarSpacing],
  );

  const scrollContentContainerStyles = [
    styles.scrollContentContainer,
    match([styles.scrollContentContainerXS, undefined]),
    match([scrollContentContainerBottomInsetStyles, undefined]),
    scrollContentContainerNavbarStyles,
  ];

  const headerStyles = match([styles.headerXS, styles.headerSM]);

  const contentContainerStyles = [
    styles.contentContainer,
    match([styles.contentContainerXS, styles.contentContainerSM]),
  ];

  const productOuterContainerStyles = [
    styles.productOuterContainer,
    match([undefined, styles.productOuterContainerSM]),
  ];

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleAddToBag = useCallback(() => {
    if (shouldDisableAddToBag) {
      return;
    }

    if (!hasLocation || isUnavailableProduct || !productForRestaurant) {
      track('share-line-item.change-location-tapped', {
        sharedLineItemProductName,
        sharedLineItemSlug: lineItemSlug,
      });
      navigateToLocationSearch();

      return;
    }

    if (isModifiableProduct && !canQuickAddModifiableProduct) {
      navigateToCustomization();

      return;
    }

    track('share-line-item.quick-add-to-bag-tapped', {
      isSharedLineItem: true,
      sharedLineItemProductName,
      sharedLineItemSlug: lineItemSlug,
    });

    if (canQuickAddModifiableProduct) {
      navigateToQuickAdd();

      return;
    }

    quickAddToBag();
  }, [
    canQuickAddModifiableProduct,
    hasLocation,
    isModifiableProduct,
    isUnavailableProduct,
    navigateToCustomization,
    navigateToLocationSearch,
    navigateToQuickAdd,
    productForRestaurant,
    quickAddToBag,
    lineItemSlug,
    sharedLineItemProductName,
    shouldDisableAddToBag,
    track,
  ]);

  const setShareLineItemAttribution = useCallback(() => {
    setSharedLineItemAttribution({
      sharedLineItemProductName,
      sharedLineItemSlug: lineItemSlug,
    });
  }, [lineItemSlug, sharedLineItemProductName]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    navigation?.setOptions({
      headerShown: !shouldHideHeaderWhileLoading && !shouldShowPartnerScreenHeader,
      title: productName,
    });
  }, [productName, navigation, shouldHideHeaderWhileLoading, shouldShowPartnerScreenHeader]);

  useEffect(setShareLineItemAttribution, [setShareLineItemAttribution]);

  useTrackEventOnFocus({
    name: 'share-line-item.viewed',
    payload: { sharedLineItemProductName, sharedLineItemSlug: lineItemSlug },
  });

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <ShareLineItemLoadingPlaceholder />;
  }

  if (partnerContent) {
    return (
      <PartnerShareLineItemScreen
        content={partnerContent}
        isAddToBagDisabled={shouldDisableAddToBag}
        isAddingToBag={isAddingProductToBag}
        lineItem={lineItem}
        modifierGroups={productModifierGroups}
        onAddToBag={handleAddToBag}
        onCustomize={navigateToCustomization}
        shouldShowCustomize={shouldShowCustomize}
        shouldShowLocationPrompt={shouldShowLocationPrompt}
        shouldShowUnavailablePrompt={shouldShowUnavailablePrompt}
      />
    );
  }

  return (
    <ScrollViewWithHeaderTitle
      style={styles.container}
      contentContainerStyle={scrollContentContainerStyles}
    >
      <Animated.View
        entering={FadeIn.duration(theme.transitions.base).delay(500)}
        style={styles.innerContent}
      >
        <Modal.Row style={headerStyles}>
          <DisplayText style={styles.text} sizeMatch={['32']}>
            {productName}
          </DisplayText>

          <BodyText style={styles.text} sizeMatch={['18']}>
            {formatMessage(messages.accountSharedBy, { name: lineItem.customerName })}
          </BodyText>
        </Modal.Row>

        <Modal.Row style={contentContainerStyles}>
          <View style={productOuterContainerStyles}>
            {shouldShowLocationPrompt ? (
              <ShareLineItemLocationAlert message={formatMessage(messages.selectLocationMessage)} />
            ) : null}

            {shouldShowUnavailablePrompt ? (
              <ShareLineItemLocationAlert message={unavailableProductMessage} palette="caution" />
            ) : null}

            <SharedLineItem
              addToBagLabel={addToBagText}
              isAddToBagDisabled={shouldDisableAddToBag}
              isAddingToBag={isAddingProductToBag}
              lineItem={cardLineItem}
              onAddToBag={handleAddToBag}
              onCustomize={shouldShowCustomize ? navigateToCustomization : undefined}
              productContainerStyle={styles.productContainer}
            />
          </View>
        </Modal.Row>
      </Animated.View>

      {minWidth.isSM ? <AppFooter /> : null}
    </ScrollViewWithHeaderTitle>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  scrollContentContainer: {
    flexGrow: 1,
    alignItems: 'center',
    paddingTop: theme.spacing['10'],
  },
  scrollContentContainerXS: {
    paddingTop: theme.spacing['6'],
  },
  innerContent: {
    width: '100%',
  },
  headerXS: {
    paddingHorizontal: theme.spacing['6'],
  },
  headerSM: {
    paddingHorizontal: theme.spacing['8'],
  },
  text: {
    textAlign: 'center',
  },
  contentContainer: {
    alignItems: 'center',
  },
  contentContainerXS: {
    paddingVertical: theme.spacing['6'],
  },
  contentContainerSM: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['20'],
  },
  productContainer: {
    flexGrow: 0, // override default style
  },
  productOuterContainer: {
    gap: theme.spacing['4'],
  },
  productOuterContainerSM: {
    maxWidth: 380,
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCardLineItem(lineItem: SharedLineItemQueryLineItem) {
  const { baseProduct: _baseProduct, ...product } = lineItem.product;

  return { ...lineItem, product };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  shareLineItemError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Share Line Item | Error',
  },
  accountSharedBy: {
    defaultMessage: 'By {name}',
    description: 'Account | Shared By',
  },
  selectLocationMessage: {
    defaultMessage: 'Select a location to order this shared item.',
    description: 'Share Line Item | Select location message',
  },
  unavailableProductMessage: {
    defaultMessage: 'This item is not available at your selected location.',
    description: 'Share Line Item | Unavailable product message',
  },
  unavailableProductForOrderMessage: {
    defaultMessage: 'This item is not available for order at your selected location.',
    description: 'Share Line Item | Unavailable product for order message',
  },
  changeLocationCta: {
    defaultMessage: 'Change location',
    description: 'Share Line Item | Change location CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ShareLineItemRouteParams = {
  lineItemSlug: string;
};

type ShareLineItemScreenV2ContentProps = {
  lineItem: SharedLineItemQueryLineItem;
  lineItemSlug: string;
};

type SharedLineItemQueryLineItem = Extract<
  GetSharedLineItemQuery['lineItemBySlug'],
  { __typename: 'LineItemSuccess' }
>['lineItem'];
